import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Headphones, LayoutDashboard, Settings2, BarChart3, Wallet, Building2, 
  Megaphone, X, Send, Loader2, Store, UserPlus, FileText, 
  Gift, Ticket, Sparkles, ArrowUpRight, Settings, Dumbbell,
  Users, ShoppingBag, ClipboardList, Apple, Bot, BookOpen, Shield,
  TrendingUp, Sliders, DollarSign, Eye, ChevronLeft, Archive, KeyRound, Landmark, Receipt, AlertTriangle, UserCog, Brain, FileCheck2, QrCode, Languages, Briefcase, Smartphone, Images
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePerms } from '../permissions/PermissionsContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import { openRagChat } from '../utils/ragChatStore';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URLs
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';
const LOGO_FULL = '/brand/ast-logo-asb606.png';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const perms = usePerms();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Platform Selection State - supports multiple levels
  const [selectedPlatform, setSelectedPlatform] = useState(null); // null, 'fzlc', 'fitness', 'admin', 'support', 'marketing', 'platform-control'
  const [selectedSubPlatform, setSelectedSubPlatform] = useState(null); // null, 'customers', 'merchants'
  const [controlSubPlatform, setControlSubPlatform] = useState(null); // For platform-control: null, 'fzlc-control'
  
  // Handle URL query parameter for view
  useEffect(() => {
    const view = searchParams.get('view');
    const platform = searchParams.get('platform');
    
    if (platform === 'fzlc' || view === 'cloudgm') {
      setSelectedPlatform('fzlc');
    } else if (platform === 'support') {
      setSelectedPlatform('support');
    } else if (platform === 'marketing' || view === 'marketing') {
      setSelectedPlatform('marketing');
    } else if (view === 'asb-admin') {
      setSelectedPlatform('admin');
    } else if (view === 'platform-control') {
      // Deep-link support: opening /dashboard?view=platform-control
      // lands directly on the "Control & Info" hub instead of the
      // top-level platform picker.  Used by sub-pages (like 606 Fitness)
      // whose Back button needs to return here — not to /dashboard root.
      setSelectedPlatform('platform-control');
      // &sub=fzlc → drill one level deeper into the CLOUDGM FZ LLC control hub
      // (used by the Back buttons of /control/cloudgm and /control/cloudgm-business)
      setControlSubPlatform(searchParams.get('sub') === 'fzlc' ? 'fzlc-control' : null);
    }
  }, [searchParams]);
  
  // Scroll to top on mount and when platform changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedPlatform, selectedSubPlatform]);

  // Platform cards for selection
  const platformCards = [
    { 
      id: 'fzlc', 
      nameAr: 'CLOUDGM FZ LLC', 
      nameEn: 'CLOUDGM FZ LLC', 
      descAr: 'منصة التجار والعملاء',
      descEn: 'Merchants & Customers Platform',
      logo: '/brand/ast-logo-cloudgm.png',
      color: '#1E9BD9',
      isInternal: true,
      isFZLC: true // New flag for FZ LC
    },
    { 
      id: 'cgm-coin', 
      nameAr: 'CGM COIN', 
      nameEn: 'CGM COIN', 
      descAr: 'العملة الرقمية',
      descEn: 'Digital Currency',
      logo: '/brand/ast-logo-cgm-coin.png',
      color: '#d4af37',
      isInternal: true,
      isCGMCoin: true
    },
    { 
      id: '606-fitness', 
      nameAr: '606 FITNESS', 
      nameEn: '606 FITNESS', 
      descAr: 'منصة اللياقة البدنية',
      descEn: 'Fitness Platform',
      logo: '/brand/ast-logo-606-fitness.png',
      color: '#10b981',
      isInternal: true,
      isFitness: true
    },
    { 
      id: '606-community', 
      nameAr: '606 COMMUNITY', 
      nameEn: '606 COMMUNITY', 
      descAr: 'منصة بوت ديسكورد',
      descEn: 'Discord Bot Platform',
      logo: '/brand/ast-logo-606-community.png',
      color: '#FF6B00',
      isInternal: true,
      isCommunity: true
    },
    { 
      id: '606-mediadocs', 
      nameAr: '606 MEDIADOCS', 
      nameEn: '606 MEDIADOCS', 
      descAr: 'المكتبة الرقمية',
      descEn: 'Digital Library',
      logo: '/brand/ast-logo-606-mediadocs.png',
      color: '#F5E6D3',
      isInternal: true,
      isMediaDocs: true
    },
    { 
      id: 'asb-admin', 
      nameAr: 'ASB606 ADMIN', 
      nameEn: 'ASB606 ADMIN', 
      descAr: 'إدارة المشرفين والنظام',
      descEn: 'Admin & System Management',
      logo: LOGO_TRIANGLE,
      color: '#d4af37',
      isInternal: true,
      isAdmin: true
    },
    {
      id: 'qr-codes',
      nameAr: 'نظام QR CODE',
      nameEn: 'QR CODE SYSTEM',
      descAr: 'إنشاء ومتابعة وإحصاء رموز QR',
      descEn: 'Create, manage & analyze QR codes',
      icon: QrCode,
      color: '#d4af37',
      isInternal: true,
      isQRCodes: true,
    },
    // iter-236 (Feb 2026) — Mobile Apps Control: Force-Update matrix
    // for every mobile app in the ASB606 umbrella (6 apps × iOS/Android).
    {
      id: 'mobile-apps',
      nameAr: 'التحكم بتطبيقات الهاتف',
      nameEn: 'Mobile Apps Control',
      descAr: 'التحكم بإصدارات التطبيقات وإجبار التحديث',
      descEn: 'Manage app versions and force-update matrix',
      icon: Smartphone,
      color: '#8b5cf6',
      isInternal: true,
      isMobileApps: true,
    },
    // iter-299m — Shared Platform Images Library (central image repo used
    // by every platform; upload once, pull by stable asset_id).
    {
      id: 'shared-assets',
      nameAr: 'قاعدة صور المنصات',
      nameEn: 'SHARED IMAGES LIBRARY',
      descAr: 'مكتبة مركزية للصور تُستخدم في كل المنصّات',
      descEn: 'Central image library shared by every platform',
      icon: Images,
      color: '#38bdf8',
      isInternal: true,
      isSharedAssets: true,
    },
    { 
      id: 'mt5-trading', 
      nameAr: '606 MT5 AI AND MANUAL TRADING', 
      nameEn: '606 MT5 AI AND MANUAL TRADING', 
      descAr: 'تداول ذكي بالذكاء الاصطناعي + تداول يدوي متعدد الحسابات',
      descEn: 'AI-Powered Trading Bot + Manual Multi-Account Trading',
      icon: TrendingUp,
      color: '#d4af37',
      route: '/meta5-trading-hub'
    },
    {
      id: 'support-control',
      nameAr: 'الدعم',
      nameEn: 'Support',
      descAr: 'التحكم بقالب واتساب وإعدادات الدعم',
      descEn: 'WhatsApp template & support settings',
      icon: Headphones,
      color: '#10b981',
      route: '/control/support',
      isSupportControl: true,
    },
  ];

  // FZ LC sub-platforms (Merchants & Customers) — الإشراف section
  //
  // Feb 2026 — restructure:
  //   • "إدراج مهام العملاء والوكلاء" (FZLC-003) MOVED into each sub-platform:
  //       – Agent tasks    → منصة التجار → الوكلاء → مهام الوكلاء
  //       – Customer tasks → منصة العملاء → طلبات الخدمة → إضافة طلب لعميل
  //   • "الترجمة القانونية" (FZLC-004) MOVED into
  //       منصة العملاء → طلبات الخدمة → خدمات الترجمة القانونية
  const CARD_SECTION = { fzlc: 'sup.fzlc', 'cgm-coin': 'sup.cgm-coin', '606-fitness': 'sup.606-fitness', '606-community': 'sup.606-community', '606-mediadocs': 'sup.606-mediadocs', 'asb-admin': 'sup.asb-admin', 'mt5-trading': 'sup.mt5' };
  const CONTROL_TARGET = { fzlc: '/control/cloudgm', 'cgm-coin': '/dashboard/cgm-coin', '606-fitness': '/dashboard/606-fitness', '606-community': '/dashboard/606-community', '606-mediadocs': '/dashboard/606-mediadocs', 'asb-admin': '/control-panel-settings', 'support-control': '/control/support', 'qr-codes': '/qr-codes', 'mobile-apps': '/control/mobile-apps', 'shared-assets': '/control/shared-assets' };
  const FZLC_TARGET = { customers: '/manage/cloudgm', merchants: '/manage/cloudgm-business', 'company-mgmt': '/manage/cloudgm-fz-llc' };
  const fzlcButtons = [
    { id: 'customers', nameAr: 'منصة العملاء', nameEn: 'Customers Platform', icon: Users, color: '#1E9BD9', isCustomers: true, code: 'FZLC-001' },
    { id: 'merchants', nameAr: 'منصة التجار', nameEn: 'Merchants Platform', icon: Store, color: '#8B5CF6', isMerchants: true, code: 'FZLC-002' },
    // iter-217c (Feb 2026) — FZLC-003 (registered companies) is now nested
    // inside the new "إدارة شركة CLOUDGM FZ LLC" hub together with the
    // certificates system. See /manage/cloudgm-fz-llc.
    { id: 'company-mgmt', nameAr: 'إدارة شركة CLOUDGM FZ LLC', nameEn: 'CLOUDGM FZ LLC Management', icon: Briefcase, color: '#10b981', route: '/manage/cloudgm-fz-llc', code: 'FZLC-MGMT' },
  ];

  // ASB606 Admin sections (Internal admin only) - الإشراف فقط
  const adminButtons = [
    { id: 'admins', nameAr: 'إدارة المشرفين', nameEn: 'Admin Management', icon: Users, route: '/control/asb606-admin/admins', code: 'ASB-001' },
    { id: 'permissions', nameAr: 'صلاحيات المشرفين والأدمن', nameEn: 'Admin & Supervisor Permissions', icon: Building2, route: '/control/asb606-admin/permissions', code: 'ASB-002' },
    { id: 'security', nameAr: 'الأمان والحماية', nameEn: 'Security & Protection', icon: Shield, route: '/control/asb606-admin/security', code: 'ASB-003' },
    { id: 'company-docs', nameAr: 'وثائق الشركة', nameEn: 'Company Documents', icon: FileText, route: '/control/asb606-admin/company-docs', code: 'ASB-004' },
    // iter-198 (Feb 2026) — Mobile-app version control (Force-Update). Writes to
    // `app_version_config` collection which the mobile Admin App reads at every
    // launch to decide whether the running build is still allowed.
    { id: 'app-versions', nameAr: 'التحكم بتطبيق الأدمن', nameEn: 'Admin App Control', icon: Smartphone, route: '/control/asb606-admin/app-versions', code: 'ASB-005' },
    // NOTE: 'قائمة الأوراق المطلوبة' moved to ControlPanelSettings (لوحة التحكم →
    // التحكم والمعلومات للمنصات → ASB606 ADMIN → قائمة الأوراق المطلوبة)
  ];

  // Support & Marketing Platform Cards (without MT5)
  // iter-286 (Feb 2026) — Support page now has ONLY two entries:
  // "مركز الدعم" (opens the WhatsApp support inbox) and
  // "تدقيق الدعم" (opens the archived-conversations audit).
  // Marketing keeps its own full platform grid (see supportMarketingPlatforms).
  const supportItems = [
    {
      id: 'center',
      nameAr: 'مركز الدعم',
      nameEn: 'Support Center',
      descAr: 'استقبال والرد على محادثات الواتساب مباشرة',
      descEn: 'Receive and respond to WhatsApp chats live',
      icon: Headphones,
      color: '#1E9BD9',
    },
    {
      id: 'audit',
      nameAr: 'تدقيق الدعم',
      nameEn: 'Support Audit',
      descAr: 'أرشيف المحادثات المُغلقة والمُدقّقة',
      descEn: 'Archive of closed & audited conversations',
      icon: FileText,
      color: '#EC4899',
    },
  ];

  const supportMarketingPlatforms = [
    { 
      id: 'fzlc', 
      nameAr: 'CLOUDGM FZ LLC', 
      nameEn: 'CLOUDGM FZ LLC', 
      descAr: 'منصة التجار والعملاء',
      descEn: 'Merchants & Customers Platform',
      logo: '/brand/ast-logo-cloudgm.png',
      color: '#1E9BD9',
    },
    { 
      id: 'cgm-coin', 
      nameAr: 'CGM COIN', 
      nameEn: 'CGM COIN', 
      descAr: 'العملة الرقمية',
      descEn: 'Digital Currency',
      logo: '/brand/ast-logo-cgm-coin.png',
      color: '#d4af37',
    },
    { 
      id: '606-fitness', 
      nameAr: '606 FITNESS', 
      nameEn: '606 FITNESS', 
      descAr: 'منصة اللياقة البدنية',
      descEn: 'Fitness Platform',
      icon: Dumbbell,
      color: '#10b981',
    },
    { 
      id: '606-community', 
      nameAr: '606 COMMUNITY', 
      nameEn: '606 COMMUNITY', 
      descAr: 'منصة بوت ديسكورد',
      descEn: 'Discord Bot Platform',
      icon: Bot,
      color: '#5865F2',
    },
    { 
      id: '606-mediadocs', 
      nameAr: '606 MEDIADOCS', 
      nameEn: '606 MEDIADOCS', 
      descAr: 'المكتبة الرقمية',
      descEn: 'Digital Library',
      icon: BookOpen,
      color: '#EC4899',
    },
    { 
      id: 'asb-admin', 
      nameAr: 'ASB606 ADMIN', 
      nameEn: 'ASB606 ADMIN', 
      descAr: 'إدارة المشرفين والنظام',
      descEn: 'Admin & System Management',
      logo: LOGO_TRIANGLE,
      color: '#d4af37',
    },
    {
      id: 'audit',
      nameAr: 'تدقيق الدعم',
      nameEn: 'Support Audit',
      descAr: 'أرشيف المحادثات المُغلقة والمُدقّقة',
      descEn: 'Archive of closed & audited chats',
      icon: FileCheck2,
      color: '#f59e0b',
    },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#030303] relative" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Background Glow Effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#d4af37]/3 rounded-full blur-[100px]" />
      </div>

      {/* Unified Header Component */}
      <DashboardHeader 
        showBackButton={!!(selectedPlatform || selectedSubPlatform || controlSubPlatform)}
        onBack={() => {
          if (controlSubPlatform) {
            setControlSubPlatform(null);
          } else if (selectedSubPlatform) {
            setSelectedSubPlatform(null);
          } else if (selectedPlatform?.startsWith('support-') || selectedPlatform?.startsWith('marketing-')) {
            const baseType = selectedPlatform.startsWith('support-') ? 'support' : 'marketing';
            setSelectedPlatform(baseType);
          } else {
            setSelectedPlatform(null);
            setControlSubPlatform(null);
          }
        }}
      />

      {/* Dynamic Breadcrumb - Shows when a platform is selected */}
      {selectedPlatform && (
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-2 text-sm">
              <button 
                onClick={() => setSelectedPlatform(null)}
                className="text-[#71717A] hover:text-[#d4af37] transition-colors"
              >
                {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
              </button>
              
              {/* Support Flow */}
              {selectedPlatform === 'support' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">{language === 'ar' ? 'مركز الدعم' : 'Support Center'}</span>
                </>
              )}
              {/* iter-286 — no more support sub-platform breadcrumb (sub-pages removed) */}
              
              {/* Marketing Flow */}
              {selectedPlatform === 'marketing' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">{language === 'ar' ? 'التسويق' : 'Marketing'}</span>
                </>
              )}
              {selectedPlatform?.startsWith('marketing-') && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <button 
                    onClick={() => setSelectedPlatform('marketing')}
                    className="text-[#71717A] hover:text-[#d4af37] transition-colors"
                  >
                    {language === 'ar' ? 'التسويق' : 'Marketing'}
                  </button>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">
                    {selectedPlatform === 'marketing-cloudgm' ? 'CLOUDGM FZ LLC (التسويق)' :
                     selectedPlatform === 'marketing-fitness' ? '606 FITNESS (التسويق)' :
                     selectedPlatform === 'marketing-cgmcoin' ? 'CGM COIN (التسويق)' :
                     selectedPlatform === 'marketing-community' ? '606 COMMUNITY (التسويق)' :
                     selectedPlatform === 'marketing-mediadocs' ? '606 MEDIADOCS (التسويق)' :
                     selectedPlatform === 'marketing-admin' ? 'ASB606 ADMIN (التسويق)' : ''}
                  </span>
                </>
              )}
              
              {/* FZLC Flow - للإشراف */}
              {selectedPlatform === 'fzlc' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">CLOUDGM FZ LLC ({language === 'ar' ? 'للإشراف' : 'Supervision'})</span>
                </>
              )}
              
              {/* Admin Flow */}
              {selectedPlatform === 'admin' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">ASB606 ADMIN</span>
                </>
              )}

              {/* Platform Control Flow */}
              {selectedPlatform === 'platform-control' && !controlSubPlatform && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">{language === 'ar' ? 'التحكم والمعلومات للمنصات' : 'Platform Control & Info'}</span>
                </>
              )}

              {/* Platform Control -> FZLC Flow */}
              {selectedPlatform === 'platform-control' && controlSubPlatform === 'fzlc-control' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <button 
                    onClick={() => setControlSubPlatform(null)}
                    className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
                  >
                    {language === 'ar' ? 'التحكم والمعلومات للمنصات' : 'Platform Control & Info'}
                  </button>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">CLOUDGM FZ LLC ({language === 'ar' ? 'للتحكم' : 'Control'})</span>
                </>
              )}

              {/* Platform Supervision Flow */}
              {selectedPlatform === 'platform-supervision' && (
                <>
                  <span className="text-[#71717A]">{'<'}</span>
                  <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">{language === 'ar' ? 'الإشراف على المنصات' : 'Platform Supervision'}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {/* Welcome Bar - Only show on main dashboard (no platform selected) */}
          {!selectedPlatform && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col md:flex-row items-center justify-between gap-4 mb-10 p-6 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10"
            >
              {/* Employee Dashboard Button - Left Side */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/employee-dashboard')}
                className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#d4af37]/20 to-[#c9a227]/20 hover:from-[#d4af37]/30 hover:to-[#c9a227]/30 border border-[#d4af37]/30 rounded-xl transition-all group order-2 md:order-1"
                data-testid="employee-dashboard-btn"
              >
                <div className="relative">
                  <UserCog className="h-6 w-6 text-[#d4af37]" />
                </div>
                <span className="text-[#d4af37] font-bold group-hover:text-[#e5c449] transition-colors">
                  {language === 'ar' ? 'لوحة تحكم الموظف والمشرف' : 'Employee & Supervisor Dashboard'}
                </span>
              </motion.button>

              {/* Welcome Message & Job Title - Right Side */}
              <div className="text-center md:text-right order-1 md:order-2">
                <h1 className="text-2xl md:text-3xl font-bold text-[#F5F5F5] mb-1" data-testid="welcome-message">
                  {language === 'ar' 
                    ? `مرحباً بك، ${user?.name || 'مستخدم'}` 
                    : `Welcome, ${user?.name || 'User'}`}
                </h1>
                <p className="text-[#d4af37] text-sm font-medium">
                  {!perms.isSuper && (user?.job_title_ar || user?.job_title)
                    ? (language === 'ar' ? (user.job_title_ar || user.job_title) : (user.job_title || user.job_title_ar))
                    : user?.role === 'admin' 
                    ? (language === 'ar' ? 'مدير النظام' : 'System Administrator')
                    : user?.role === 'supervisor'
                    ? (language === 'ar' ? 'مشرف' : 'Supervisor')
                    : (language === 'ar' ? 'مستخدم' : 'User')}
                </p>
              </div>
            </motion.div>
          )}

          {/* Page Title Section */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-4 tracking-tight">
              {selectedPlatform === 'admin'
                ? (language === 'ar' ? 'ASB606 Admin - الإدارة' : 'ASB606 Admin - Management')
                : selectedPlatform === 'fzlc' && !selectedSubPlatform
                ? (language === 'ar' ? 'CLOUDGM FZ LLC' : 'CLOUDGM FZ LLC')
                : selectedPlatform === 'fzlc' && selectedSubPlatform === 'customers'
                ? (language === 'ar' ? 'منصة العملاء' : 'Customers Platform')
                : selectedPlatform === 'fzlc' && selectedSubPlatform === 'merchants'
                ? (language === 'ar' ? 'منصة التجار' : 'Merchants Platform')
                : selectedPlatform === 'support'
                ? (language === 'ar' ? 'مركز الدعم' : 'Support Center')
                : selectedPlatform === 'marketing'
                ? (language === 'ar' ? 'التسويق' : 'Marketing')
                : selectedPlatform === 'platform-control'
                ? (language === 'ar' ? 'التحكم والمعلومات للمنصات' : 'Platform Control & Info')
                : selectedPlatform?.startsWith('support-')
                ? (language === 'ar' ? 'قريباً - الدعم' : 'Coming Soon - Support')
                : selectedPlatform?.startsWith('marketing-')
                ? (language === 'ar' ? 'قريباً - التسويق' : 'Coming Soon - Marketing')
                : (language === 'ar' ? 'إدارة شاملة لأعمالك' : 'Complete Business Management')}
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              {selectedPlatform === 'admin'
                ? (language === 'ar' 
                    ? 'إدارة المشرفين والنظام' 
                    : 'Admin and system management')
                : selectedPlatform === 'fzlc' && !selectedSubPlatform
                ? (language === 'ar' 
                    ? 'اختر المنصة للإشراف' 
                    : 'Select Platform to Supervise')
                : selectedPlatform === 'fzlc' && selectedSubPlatform === 'customers'
                ? ''
                : selectedPlatform === 'fzlc' && selectedSubPlatform === 'merchants'
                ? ''
                : selectedPlatform === 'support'
                ? (language === 'ar' 
                    ? 'اختر المنصة لإدارة الدعم' 
                    : 'Select platform to manage support')
                : selectedPlatform === 'marketing'
                ? (language === 'ar' 
                    ? 'اختر المنصة لإدارة التسويق' 
                    : 'Select platform to manage marketing')
                : selectedPlatform === 'platform-control'
                ? (language === 'ar' 
                    ? 'إدارة الإعدادات والمعلومات لكل منصة' 
                    : 'Manage settings and info for each platform')
                : (language === 'ar' 
                    ? 'تحكم في جميع منصاتك من مكان واحد مع أدوات متقدمة وتقارير شاملة' 
                    : 'Control all your platforms from one place with advanced tools and comprehensive reports')}
            </p>
          </motion.div>

          {/* Platform Selection View - Show when no platform selected */}
          {!selectedPlatform && (
            <>
              {/* Section Title - Choose Platform (hidden when the user has no supervision cards) */}
              {platformCards.some(p => !p.isSupportControl && !p.isQRCodes && !p.isMobileApps && !p.isSharedAssets && perms.canAccess(CARD_SECTION[p.id])) && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-center mb-8"
              >
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'اختر المنصة للإشراف' : 'Choose Platform to Supervise'}
                </h3>
              </motion.div>
              )}

              {/* Platform Selection Cards */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
              >
                {/* iter-237 (Feb 2026) — Hide Mobile Apps Control from the
                    main dashboard grid; it lives ONLY inside
                    "Platform Control & Info" (below at line ~1037). */}
                {platformCards.filter(p => !p.isSupportControl && !p.isQRCodes && !p.isMobileApps && !p.isSharedAssets && perms.canAccess(CARD_SECTION[p.id])).map((platform) => (
                  <motion.button
                    key={platform.id}
                    variants={cardVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (platform.isInternal) {
                        if (platform.isAdmin) {
                          setSelectedPlatform('admin');
                          setSelectedSubPlatform(null);
                        } else if (platform.isFitness) {
                          navigate('/control/f606');
                        } else if (platform.isFZLC) {
                          setSelectedPlatform('fzlc');
                          setSelectedSubPlatform(null);
                        } else if (platform.isCGMCoin) {
                          setSelectedPlatform('cgm-coin');
                          setSelectedSubPlatform(null);
                        } else if (platform.isCommunity) {
                          setSelectedPlatform('606-community');
                          setSelectedSubPlatform(null);
                        } else if (platform.isMediaDocs) {
                          setSelectedPlatform('606-mediadocs');
                          setSelectedSubPlatform(null);
                        }
                      } else {
                        navigate(platform.route);
                      }
                    }}
                    className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                    style={{
                      '--hover-color': platform.color,
                    }}
                    data-testid={`platform-${platform.id}`}
                  >
                    {/* Hover Glow */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                      style={{ 
                        background: `radial-gradient(circle at center, ${platform.color}10 0%, transparent 70%)` 
                      }}
                    />
                    
                    {/* Border Glow on Hover */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ 
                        boxShadow: `inset 0 0 0 1px ${platform.color}60, 0 8px 32px ${platform.color}20` 
                      }}
                    />
                    
                    {/* Glass Shine */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="relative">
                      {/* Platform Icon/Logo */}
                      <div 
                        className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 overflow-hidden"
                        style={{ 
                          backgroundColor: `${platform.color}15`,
                          border: `1px solid ${platform.color}30`
                        }}
                      >
                        {platform.logo ? (
                          <img 
                            src={platform.logo}
                            alt={platform.nameEn}
                            className="w-12 h-12 object-contain"
                          />
                        ) : platform.icon ? (
                          <platform.icon className="w-8 h-8" style={{ color: platform.color }} />
                        ) : null}
                      </div>
                      
                      {/* Platform Name */}
                      <h3 
                        className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:transition-colors"
                        style={{ '--tw-text-opacity': 1 }}
                      >
                        <span 
                          className="group-hover:text-[var(--hover-color)]" 
                          style={{ '--hover-color': platform.color }}
                          dir={platform.nameEn.startsWith('606') || platform.nameEn.includes(' 606') ? 'ltr' : undefined}
                        >
                          {language === 'ar' ? platform.nameAr : platform.nameEn}
                        </span>
                      </h3>
                      
                      {/* Platform Description */}
                      <p className="text-[#71717A] text-xs">
                        {language === 'ar' ? platform.descAr : platform.descEn}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>

              {/* Divider */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-4 my-10 max-w-5xl mx-auto"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <span className="text-[#71717A] text-sm">{language === 'ar' ? 'خدمات إضافية' : 'Additional Services'}</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </motion.div>

              {/* Support & Marketing Buttons - Same size and grid as platform cards */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
              >
                {/* Support Button */}
                {perms.canAccess('sup.support') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedPlatform('support');
                    setSelectedSubPlatform(null);
                  }}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#EC4899]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#EC4899', direction: 'rtl' }}
                  data-testid="support-btn"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #EC489910 0%, transparent 70%)' }} />
                  
                  {/* Border Glow on Hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #EC489960, 0 8px 32px #EC489920' }} />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#EC4899]/15 border border-[#EC4899]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Headphones className="w-8 h-8 text-[#EC4899]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#EC4899] transition-colors">
                      {language === 'ar' ? 'مركز الدعم' : 'Support Center'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'محادثات الواتساب والتدقيق' : 'WhatsApp chats & audit'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* Marketing Button */}
                {perms.canAccess('marketing') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedPlatform('marketing');
                    setSelectedSubPlatform(null);
                  }}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#F97316]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#F97316', direction: 'rtl' }}
                  data-testid="marketing-btn"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #F9731610 0%, transparent 70%)' }} />
                  
                  {/* Border Glow on Hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #F9731660, 0 8px 32px #F9731620' }} />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Megaphone className="w-8 h-8 text-[#F97316]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#F97316] transition-colors">
                      {language === 'ar' ? 'التسويق' : 'Marketing'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'مركز التسويق والإعلانات' : 'Marketing & Ads Center'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* Finance Button */}
                {perms.canAccess('finance') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/dashboard/finance')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#10B981]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#10B981', direction: 'rtl' }}
                  data-testid="finance-btn"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #10B98110 0%, transparent 70%)' }} />
                  
                  {/* Border Glow on Hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #10B98160, 0 8px 32px #10B98120' }} />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <DollarSign className="w-8 h-8 text-[#10B981]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#10B981] transition-colors">
                      {language === 'ar' ? 'المالية' : 'Finance'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'الإدارة المالية والدفعات' : 'Finance & Payments'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* Order Tracking Button */}
                {/* Order Tracking moved into: Merchant Platform Supervision → Order Tracking tab */}

                {/* Platform Supervision Button - New */}
                {perms.canAccess('platform-supervision') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedPlatform('platform-supervision');
                    setSelectedSubPlatform(null);
                  }}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#06B6D4]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#06B6D4', direction: 'rtl' }}
                  data-testid="platform-supervision-btn"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #06B6D410 0%, transparent 70%)' }} />
                  
                  {/* Border Glow on Hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #06B6D460, 0 8px 32px #06B6D420' }} />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#06B6D4]/15 border border-[#06B6D4]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Eye className="w-8 h-8 text-[#06B6D4]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#06B6D4] transition-colors">
                      {language === 'ar' ? 'متابعة مؤشر الأداء' : 'Performance Monitoring'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'مؤشرات أداء المنصات' : 'Platform Performance Indicators'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* Platform Control & Info Button */}
                {perms.canAccess('platform-control') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedPlatform('platform-control');
                    setSelectedSubPlatform(null);
                  }}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#8B5CF6]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#8B5CF6', direction: 'rtl' }}
                  data-testid="platform-control-btn"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #8B5CF610 0%, transparent 70%)' }} />
                  
                  {/* Border Glow on Hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #8B5CF660, 0 8px 32px #8B5CF620' }} />
                  
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Settings2 className="w-8 h-8 text-[#8B5CF6]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#8B5CF6] transition-colors">
                      {language === 'ar' ? 'التحكم والمعلومات' : 'Control & Info'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'تحكم لجميع المنصات' : 'All Platforms Control'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* Archive Button — soft-delete + restore hub */}
                {perms.canAccess('archive') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/control/archive')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#d4af37]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#d4af37', direction: 'rtl' }}
                  data-testid="archive-btn"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #d4af3710 0%, transparent 70%)' }} />
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #d4af3760, 0 8px 32px #d4af3720' }} />
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Archive className="w-8 h-8 text-[#d4af37]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#d4af37] transition-colors">
                      {language === 'ar' ? 'الأرشيف' : 'Archive'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'سجلّات مؤرشفة قبل الحذف' : 'Archived records before deletion'}
                    </p>
                  </div>
                </motion.button>
                )}

                {/* AI Control Center — central toggle for every AI feature */}
                {perms.canAccess('ai') && (
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/control/ai')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#A78BFA]/50 hover:bg-white/[0.04] overflow-hidden"
                  style={{ '--hover-color': '#A78BFA', direction: 'rtl' }}
                  data-testid="ai-control-btn"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'radial-gradient(circle at center, #A78BFA10 0%, transparent 70%)' }} />
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: 'inset 0 0 0 1px #A78BFA60, 0 8px 32px #A78BFA20' }} />
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Brain className="w-8 h-8 text-[#A78BFA]" />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:text-[#A78BFA] transition-colors">
                      Cloud GM AI Control Hub
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'تشغيل/إيقاف كل الذكاء الاصطناعي' : 'Toggle every AI feature'}
                    </p>
                  </div>
                </motion.button>
                )}
              </motion.div>
            </>
          )}

          {/* Platform Supervision Selection View - Empty for now */}
          {selectedPlatform === 'platform-supervision' && (
            <>
              {/* Section Title */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#06B6D4]/15 border border-[#06B6D4]/30 flex items-center justify-center">
                  <Eye className="w-8 h-8 text-[#06B6D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'متابعة مؤشر الأداء' : 'Performance Monitoring'}
                </h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'قريباً...' : 'Coming soon...'}
                </p>
              </motion.div>

              {/* Empty State */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-white/[0.02] border border-white/10 flex items-center justify-center">
                  <Eye className="w-12 h-12 text-[#71717A]" />
                </div>
                <p className="text-[#71717A] text-lg">
                  {language === 'ar' ? 'هذا القسم قيد التطوير' : 'This section is under development'}
                </p>
              </motion.div>
            </>
          )}

          {/* ASB606 Admin Sections View */}
          {selectedPlatform === 'admin' && (
            <>
              {/* Section Title */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'إدارة النظام' : 'System Management'}
                </h3>
              </motion.div>

              {/* Admin Buttons Grid */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
              >
                {adminButtons.filter(btn => perms.canViewRoute(btn.route)).map((btn) => (
                  <motion.button
                    key={btn.id}
                    variants={cardVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(btn.route)}
                    className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#d4af37]/50 hover:bg-white/[0.04] hover:shadow-[0_8px_32px_rgba(212,175,55,0.12)] overflow-hidden"
                    data-testid={`btn-${btn.id}`}
                  >
                    {/* Screen Code Badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#d4af37]/10 rounded-md">
                      <span className="text-[10px] font-mono text-[#d4af37]">{btn.code}</span>
                    </div>
                    
                    {/* Hover Glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/0 via-[#d4af37]/0 to-[#d4af37]/0 group-hover:from-[#d4af37]/5 group-hover:via-transparent group-hover:to-[#d4af37]/5 transition-all duration-500" />
                    
                    {/* Glass Shine */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="relative">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-[#d4af37]/20 transition-all duration-300">
                        <btn.icon className="h-8 w-8 text-[#d4af37]" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-[#F5F5F5] font-bold text-sm group-hover:text-[#d4af37] transition-colors">
                        {language === 'ar' ? btn.nameAr : btn.nameEn}
                      </h3>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}

          {/* Platform Control & Info Selection View - Show only when no sub-platform selected */}
          {selectedPlatform === 'platform-control' && !controlSubPlatform && (
            <>
              {/* Section Title */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 flex items-center justify-center">
                  <Settings2 className="w-8 h-8 text-[#8B5CF6]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'التحكم والمعلومات للمنصات' : 'Platform Control & Info'}
                </h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'إدارة الإعدادات والمعلومات لكل منصة' : 'Manage settings and info for each platform'}
                </p>
              </motion.div>

              {/* Platform Control Grid - Same 6 platforms */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
              >
                {platformCards.filter(p => !p.id.includes('mt5') && perms.canViewRoute(CONTROL_TARGET[p.id] || '/')).map((platform) => (
                  <motion.button
                    key={`control-${platform.id}`}
                    variants={cardVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // Navigate to control page based on platform
                      if (platform.isFZLC) {
                        // Show sub-platforms for FZLC Control
                        setControlSubPlatform('fzlc-control');
                      } else if (platform.isCGMCoin) {
                        navigate('/dashboard/cgm-coin');
                      } else if (platform.isFitness) {
                        navigate('/dashboard/606-fitness');
                      } else if (platform.isCommunity) {
                        navigate('/dashboard/606-community');
                      } else if (platform.isMediaDocs) {
                        navigate('/dashboard/606-mediadocs');
                      } else if (platform.isAdmin) {
                        navigate('/control-panel-settings');
                      } else if (platform.isSupportControl) {
                        navigate('/control/support');
                      } else if (platform.isQRCodes) {
                        navigate('/qr-codes');
                      } else if (platform.isMobileApps) {
                        // iter-236 — New Mobile Apps Control hub.
                        navigate('/control/mobile-apps');
                      } else if (platform.isSharedAssets) {
                        navigate('/control/shared-assets');
                      }
                    }}
                    className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                    style={{ 
                      '--hover-color': platform.color,
                      direction: 'ltr'
                    }}
                    data-testid={`control-${platform.id}`}
                  >
                    {/* Hover Glow */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" 
                      style={{ background: `radial-gradient(circle at center, ${platform.color}10 0%, transparent 70%)` }} 
                    />
                    
                    {/* Border Glow on Hover */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                      style={{ boxShadow: `inset 0 0 0 1px ${platform.color}60, 0 8px 32px ${platform.color}20` }} 
                    />
                    
                    <div className="relative">
                      <div 
                        className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 border"
                        style={{ 
                          backgroundColor: `${platform.color}15`,
                          borderColor: `${platform.color}30`
                        }}
                      >
                        {platform.logo ? (
                          <img 
                            src={platform.logo} 
                            alt={platform.nameEn} 
                            className="w-10 h-10 object-contain"
                          />
                        ) : platform.icon ? (
                          <platform.icon className="h-8 w-8" style={{ color: platform.color }} strokeWidth={1.5} />
                        ) : null}
                      </div>
                      <h3 
                        className="text-[#F5F5F5] font-bold text-sm group-hover:transition-colors"
                        style={{ direction: 'ltr' }}
                      >
                        {platform.nameEn}
                      </h3>
                      <p className="text-[#71717A] text-xs mt-1" dir="rtl">
                        {language === 'ar' ? 'التحكم والمعلومات' : 'Control & Info'}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}

          {/* FZLC Control Sub-platforms Selection */}
          {selectedPlatform === 'platform-control' && controlSubPlatform === 'fzlc-control' && (
            <>
              {/* Section Title - No logo, just text */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'اختر المنصة للتحكم' : 'Select Platform to Control'}
                </h3>
              </motion.div>

              {/* FZLC Control Sub-platforms Grid */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
              >
                {/* Customers Platform - Control */}
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/control/cloudgm')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                  data-testid="control-fzlc-customers"
                >
                  {/* Hover Glow */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                    style={{ background: 'radial-gradient(circle at center, #06b6d410 0%, transparent 70%)' }}
                  />
                  
                  {/* Border Glow on Hover */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: 'inset 0 0 0 1px #06b6d460, 0 8px 32px #06b6d420' }}
                  />
                  
                  <div className="relative">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 bg-[#06b6d4]/15 border border-[#06b6d4]/30">
                      <Users className="h-10 w-10 text-[#06b6d4]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-lg mb-2 group-hover:text-[#06b6d4] transition-colors">
                      {language === 'ar' ? 'منصة العملاء' : 'Customers Platform'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'التحكم والمعلومات' : 'Control & Info'}
                    </p>
                  </div>
                </motion.button>

                {/* Merchants Platform - Control */}
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/control/cloudgm-business')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                  data-testid="control-fzlc-merchants"
                >
                  {/* Hover Glow */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                    style={{ background: 'radial-gradient(circle at center, #a855f710 0%, transparent 70%)' }}
                  />
                  
                  {/* Border Glow on Hover */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: 'inset 0 0 0 1px #a855f760, 0 8px 32px #a855f720' }}
                  />
                  
                  <div className="relative">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 bg-[#a855f7]/15 border border-[#a855f7]/30">
                      <Store className="h-10 w-10 text-[#a855f7]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-lg mb-2 group-hover:text-[#a855f7] transition-colors">
                      {language === 'ar' ? 'منصة التجار' : 'Merchants Platform'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'التحكم والمعلومات' : 'Control & Info'}
                    </p>
                  </div>
                </motion.button>

                {/* iter-251 (Feb 2026) — CLOUDGM FZ-LLC Management
                    Groups shared catalogs/dropdowns used across platforms
                    (starts with the company-services catalog moved from
                    the merchant dropdown-lists). */}
                <motion.button
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/control/cloudgm-fzlc-management')}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                  data-testid="control-fzlc-management"
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                    style={{ background: 'radial-gradient(circle at center, #d4af3710 0%, transparent 70%)' }}
                  />
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: 'inset 0 0 0 1px #d4af3760, 0 8px 32px #d4af3720' }}
                  />
                  <div className="relative">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 bg-[#d4af37]/15 border border-[#d4af37]/30">
                      <Building2 className="h-10 w-10 text-[#d4af37]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[#F5F5F5] font-bold text-lg mb-2 group-hover:text-[#d4af37] transition-colors">
                      {language === 'ar' ? 'إدارة الشركة CLOUDGM FZ-LLC' : 'Manage CLOUDGM FZ-LLC'}
                    </h3>
                    <p className="text-[#71717A] text-xs">
                      {language === 'ar' ? 'قوائم وكتالوجات مشتركة' : 'Shared lists & catalogs'}
                    </p>
                  </div>
                </motion.button>

                {/* iter-218c (Feb 2026) — "التحكم بإدارة الشركة" button
                    moved to ASB606 ADMIN (Control) — see `ControlPanelSettings.js`. */}
              </motion.div>
            </>
          )}

          {/* Support Platform Selection View */}
          {selectedPlatform === 'support' && (
            <>
              {/* Section Title */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#EC4899]/15 border border-[#EC4899]/30 flex items-center justify-center">
                  <Headphones className="w-8 h-8 text-[#EC4899]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'مركز الدعم' : 'Support Center'}
                </h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'محادثات الواتساب والأرشيف المُدقّق' : 'WhatsApp chats & audited archive'}
                </p>
              </motion.div>

              {/* Support Items Grid — iter-286: 2 items only */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto"
              >
                {supportItems.filter(item => perms.canViewRoute(item.id === 'center' ? '/support/customers' : '/support/audit')).map((item, index) => (
                  <motion.button
                    key={item.id}
                    variants={cardVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (item.id === 'center') {
                        // Directly opens the WhatsApp support inbox.
                        navigate('/support/customers');
                      } else if (item.id === 'audit') {
                        navigate('/support/audit');
                      }
                    }}
                    className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                    style={{ '--hover-color': item.color }}
                    data-testid={`support-item-${item.id}`}
                  >
                    {/* Screen Code Badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md" style={{ backgroundColor: `${item.color}15` }}>
                      <span className="text-[10px] font-mono" style={{ color: item.color }}>SUP-{String(index + 1).padStart(3, '0')}</span>
                    </div>

                    {/* Hover Glow */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: `radial-gradient(circle at center, ${item.color}10 0%, transparent 70%)` }} />

                    {/* Border Glow on Hover */}
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: `inset 0 0 0 1px ${item.color}60, 0 8px 32px ${item.color}20` }} />

                    <div className="relative">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300" style={{ backgroundColor: `${item.color}15`, border: `1px solid ${item.color}30` }}>
                        <item.icon className="w-10 h-10" style={{ color: item.color }} />
                      </div>
                      <h3 className="text-[#F5F5F5] font-bold text-lg mb-2 group-hover:transition-colors">
                        <span className="group-hover:text-[var(--hover-color)]" style={{ '--hover-color': item.color }}>
                          {language === 'ar' ? item.nameAr : item.nameEn}
                        </span>
                      </h3>
                      <p className="text-[#71717A] text-sm">
                        {language === 'ar' ? item.descAr : item.descEn}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}

          {/* iter-286 — Support sub-platform 'coming soon' block removed
              (all support platform buttons were consolidated into 2 items:
               مركز الدعم + تدقيق الدعم — both route to real pages). */}

          {/* Marketing Platform Selection View */}
          {selectedPlatform === 'marketing' && (
            <>
              {/* Section Title */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-[#F97316]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'اختر المنصة للتسويق' : 'Select Platform for Marketing'}
                </h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'إدارة الحملات التسويقية لكل منصة' : 'Manage marketing campaigns for each platform'}
                </p>
              </motion.div>

              {/* Marketing Platforms Grid */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
              >
                {supportMarketingPlatforms.filter(p => p.id !== 'audit').map((platform, index) => (
                  <motion.button
                    key={platform.id}
                    variants={cardVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // CLOUDGM FZ LLC goes to /marketing page
                      if (platform.id === 'fzlc') {
                        navigate('/marketing');
                      } else {
                        // All other marketing pages show "Coming Soon" for now
                        setSelectedPlatform(`marketing-${platform.id}`);
                      }
                    }}
                    className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                    style={{ '--hover-color': platform.color }}
                    data-testid={`marketing-platform-${platform.id}`}
                  >
                    {/* Screen Code Badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md" style={{ backgroundColor: `${platform.color}15` }}>
                      <span className="text-[10px] font-mono" style={{ color: platform.color }}>MKT-{String(index + 1).padStart(3, '0')}</span>
                    </div>
                    
                    {/* Hover Glow */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: `radial-gradient(circle at center, ${platform.color}10 0%, transparent 70%)` }} />
                    
                    {/* Border Glow on Hover */}
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: `inset 0 0 0 1px ${platform.color}60, 0 8px 32px ${platform.color}20` }} />
                    
                    <div className="relative">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 overflow-hidden" style={{ backgroundColor: `${platform.color}15`, border: `1px solid ${platform.color}30` }}>
                        {platform.logo ? (
                          <img src={platform.logo} alt={platform.nameEn} className="w-12 h-12 object-contain" />
                        ) : platform.icon ? (
                          <platform.icon className="w-8 h-8" style={{ color: platform.color }} />
                        ) : null}
                      </div>
                      <h3 className="text-[#F5F5F5] font-bold text-sm mb-1 group-hover:transition-colors" style={{ '--tw-text-opacity': 1 }}>
                        <span className="group-hover:text-[var(--hover-color)]" style={{ '--hover-color': platform.color }}>
                          {language === 'ar' ? platform.nameAr : platform.nameEn}
                        </span>
                      </h3>
                      <p className="text-[#71717A] text-xs">
                        {language === 'ar' ? platform.descAr : platform.descEn}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}

          {/* Marketing Coming Soon Pages */}
          {selectedPlatform?.startsWith('marketing-') && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center">
                  <Megaphone className="w-10 h-10 text-[#F97316]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'قريباً' : 'Coming Soon'}
                </h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'صفحة التسويق لهذه المنصة قيد التطوير' : 'Marketing page for this platform is under development'}
                </p>
              </motion.div>
            </>
          )}

          {/* CGM Coin View - Coming Soon */}
          {selectedPlatform === 'cgm-coin' && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center">
                  <img 
                    src={'/brand/ast-logo-cgm-coin.png'} 
                    alt="CGM COIN" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]" dir="ltr">CGM COIN</h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'قريباً - التحكم بمنصة العملة الرقمية' : 'Coming Soon - Digital Currency Platform Control'}
                </p>
              </motion.div>
            </>
          )}

          {/* 606 Community View - Coming Soon */}
          {selectedPlatform === '606-community' && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center overflow-hidden">
                  <img 
                    src={'/brand/ast-logo-606-community.png'} 
                    alt="606 COMMUNITY" 
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]" dir="ltr">606 COMMUNITY</h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'قريباً - التحكم ببوت ديسكورد' : 'Coming Soon - Discord Bot Control'}
                </p>
              </motion.div>
            </>
          )}

          {/* 606 MediaDocs View - Coming Soon */}
          {selectedPlatform === '606-mediadocs' && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-[#F5E6D3]/15 border border-[#F5E6D3]/30 flex items-center justify-center overflow-hidden">
                  <img 
                    src={'/brand/ast-logo-606-mediadocs.png'} 
                    alt="606 MEDIADOCS" 
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5]" dir="ltr">606 MEDIADOCS</h3>
                <p className="text-[#71717A] text-sm mt-2">
                  {language === 'ar' ? 'قريباً - التحكم بالمكتبة الرقمية' : 'Coming Soon - Digital Library Control'}
                </p>
              </motion.div>
            </>
          )}

          {/* FZ LC Sections View - Main Level (Customers vs Merchants) - للإشراف */}
          {selectedPlatform === 'fzlc' && !selectedSubPlatform && (
            <>
              {/* Section Title - No logo, just text */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <h3 className="text-xl font-bold text-[#F5F5F5]">
                  {language === 'ar' ? 'اختر المنصة للإشراف' : 'Select Platform to Supervise'}
                </h3>
              </motion.div>

              {/* FZ LC Sub-platforms Grid — Supervision Platforms only
                * (Feb 2026: "Other" group removed — Manual Tasks & Legal
                * Translation moved into their respective sub-platforms.)
                */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-4xl mx-auto space-y-8"
              >
                <div>
                  <h2 className="text-white/60 text-xs font-semibold tracking-widest mb-4 uppercase flex items-center gap-2">
                    <span className="w-1 h-4 rounded bg-[#d4af37]" />
                    {language === 'ar' ? 'منصات الإشراف' : 'Supervision Platforms'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {fzlcButtons.filter(btn => perms.canViewRoute(FZLC_TARGET[btn.id] || '/')).map((btn) => (
                        <motion.button
                          key={btn.id}
                          variants={cardVariants}
                          whileHover={{ y: -8, scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            // توجيه مباشر لصفحة الإشراف
                            if (btn.isCustomers) {
                              navigate('/manage/cloudgm');
                            } else if (btn.isMerchants) {
                              navigate('/manage/cloudgm-business');
                            } else if (btn.route) {
                              // iter-217c — buttons with an explicit `route`
                              // (e.g. FZLC-MGMT) navigate directly.
                              navigate(btn.route);
                            }
                          }}
                          className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
                          style={{ '--btn-color': btn.color }}
                          data-testid={`btn-${btn.id}`}
                        >
                          {/* Screen Code Badge */}
                          <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md" style={{ backgroundColor: `${btn.color}15` }}>
                            <span className="text-[10px] font-mono" style={{ color: btn.color }}>{btn.code}</span>
                          </div>
                          
                          {/* Hover Glow */}
                          <div 
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                            style={{ background: `radial-gradient(circle at center, ${btn.color}10 0%, transparent 70%)` }}
                          />
                          
                          {/* Border Glow on Hover */}
                          <div 
                            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{ boxShadow: `inset 0 0 0 1px ${btn.color}60, 0 8px 32px ${btn.color}20` }}
                          />
                          
                          <div className="relative">
                            <div 
                              className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300"
                              style={{ backgroundColor: `${btn.color}15`, border: `1px solid ${btn.color}30` }}
                            >
                              <btn.icon className="h-10 w-10" style={{ color: btn.color }} strokeWidth={1.5} />
                            </div>
                            <h3 className="text-[#F5F5F5] font-bold text-lg mb-2 group-hover:transition-colors" style={{ '--hover-color': btn.color }}>
                              <span className="group-hover:text-[var(--btn-color)]">
                                {language === 'ar' ? btn.nameAr : btn.nameEn}
                              </span>
                            </h3>
                          </div>
                        </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Footer Brand */}
      <footer className="py-12 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="flex flex-col items-center"
          >
            <img 
              src={LOGO_FULL}
              alt="ASB606 Platforms Group"
              className="h-16 object-contain opacity-60 hover:opacity-100 transition-opacity"
            />
            <p className="text-[#71717A] text-xs mt-4">
              © 2026 ASB606 Platforms Group. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

// Notification Item Component
function NotificationItem({ icon: Icon, color, title, count, onClick }) {
  return (
    <motion.button
      whileHover={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}
      onClick={onClick}
      className="w-full px-4 py-3 flex items-center gap-3 text-start border-b border-white/5 last:border-0 transition-colors"
    >
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#F5F5F5] text-sm font-medium">{title}</p>
        <p className="text-[#71717A] text-xs">{count} pending</p>
      </div>
      <ChevronLeft className="h-4 w-4 text-[#71717A]" />
    </motion.button>
  );
}
